 

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../../API/axiosNeatex';

interface TaskAssignment {
  id: string;
  taskId: string;
  resourceType: string;
  resourceId: string;
  resourceName: string;
  resourceName2: string;
  role: string;
  isOwner: boolean;
  estimatedHours?: number;
  notes?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

// ==================== Async Thunks ====================

// 🔵 جلب جميع التعيينات لمهمة معينة
export const getTaskAssignmentsByTaskId = createAsyncThunk(
  'TaskAssignments/getTaskAssignmentsByTaskId',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get(`Task/GetTaskAssignmentByTaskId?taskId=${taskId}`);
      if (data?.succeeded) return data.data;
      return [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 🟢 إضافة تعيين
export const addTaskAssignment = createAsyncThunk(
  'TaskAssignments/addTaskAssignment',
  // async (item: Omit<TaskAssignment, 'id' | 'createdBy' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
  async (item: any, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/AddTaskAssignment', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في إضافة التعيين');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ✏️ تعديل تعيين
export const updateTaskAssignment = createAsyncThunk(
  'TaskAssignments/updateTaskAssignment',
  // async (item: TaskAssignment, { rejectWithValue }) => {
  async (item: any, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/UpdateTaskAssignment', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في تحديث التعيين');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 🗑️ حذف تعيين
export const deleteTaskAssignment = createAsyncThunk(
  'TaskAssignments/deleteTaskAssignment',
  async (assignmentId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post(`Task/DeleteTaskAssignment?Id=${assignmentId}`);
      if (data?.succeeded) return assignmentId;
      return rejectWithValue(data?.message || 'فشل في حذف التعيين');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==================== Slice ====================

interface TaskAssignmentsState {
  taskAssignmentList: TaskAssignment[];
  loading: boolean;
  error: string | null;
}

const initialState: TaskAssignmentsState = {
  taskAssignmentList: [],
  loading: false,
  error: null
};

const TaskAssignmentsSlice = createSlice({
  name: 'TaskAssignments',
  initialState,
  reducers: {
    clearTaskAssignments: state => {
      state.taskAssignmentList = [];
      state.error = null;
      state.loading = false;
    },
    addLocalTaskAssignment: (state, action: PayloadAction<TaskAssignment>) => {
      state.taskAssignmentList.unshift(action.payload);
    },
    updateLocalTaskAssignment: (state, action: PayloadAction<TaskAssignment>) => {
      const index = state.taskAssignmentList.findIndex(a => a.id === action.payload.id);
      if (index !== -1) state.taskAssignmentList[index] = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      // 🔵 Get
      .addCase(getTaskAssignmentsByTaskId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTaskAssignmentsByTaskId.fulfilled, (state, action) => {
        state.loading = false;
        state.taskAssignmentList = action.payload;
      })
      .addCase(getTaskAssignmentsByTaskId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // 🟢 Add
      .addCase(addTaskAssignment.fulfilled, (state, action) => {
        state.taskAssignmentList.unshift(action.payload);
      })
      .addCase(addTaskAssignment.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // ✏️ Update
      .addCase(updateTaskAssignment.fulfilled, (state, action) => {
        const index = state.taskAssignmentList.findIndex(a => a.id === action.payload.id);
        if (index !== -1) state.taskAssignmentList[index] = action.payload;
      })
      .addCase(updateTaskAssignment.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // 🗑️ Delete
      .addCase(deleteTaskAssignment.fulfilled, (state, action) => {
        state.taskAssignmentList = state.taskAssignmentList.filter(a => a.id !== action.payload);
      })
      .addCase(deleteTaskAssignment.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

export const { clearTaskAssignments, addLocalTaskAssignment, updateLocalTaskAssignment } = TaskAssignmentsSlice.actions;

export default TaskAssignmentsSlice.reducer;
